/**
 * Transform service — loading/saving print transform configs.
 */
import type { Transform } from '../stores/types';
import type { Product } from '../stores/appStore';
import { initializeTransforms } from './renderService';
import * as DataService from './dataService';
import type { PrintConfig } from './dataService';

/** Load print with its config, applying default transforms first then overriding with saved config */
export async function loadPrintWithConfig(
  file: { url: string; name: string },
  products: Product[],
  setTransforms: (fn: (prev: Record<string, Transform>) => Record<string, Transform>) => void,
  setProductTransforms: (
    fn: (prev: Record<string, Transform>) => Record<string, Transform>,
  ) => void,
  autoLoadTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  isPrintLoadedRef: React.MutableRefObject<boolean>,
): Promise<void> {
  // 1. Set default transforms
  const defTransforms = await initializeTransforms(file, products);
  const defProdTransforms = await initializeTransforms(file, products);

  setTransforms(() => defTransforms);
  setProductTransforms(() => defProdTransforms);

  // 2. Timer to load saved config
  if (autoLoadTimerRef.current) clearTimeout(autoLoadTimerRef.current);

  autoLoadTimerRef.current = setTimeout(async () => {
    const saved = (await DataService.loadPrintsConfig(file.name)) as PrintConfig | null;
    if (saved && saved.transforms) {
      setTransforms((prev) => ({ ...prev, ...saved.transforms }));
      setProductTransforms((prev) => ({ ...prev, ...(saved.productTransforms || {}) }));
    }
    isPrintLoadedRef.current = true;
  }, 500);
}

/** Save print config to server */
export async function savePrintConfig(
  printName: string,
  transforms: Record<string, Transform>,
  productTransforms: Record<string, Transform>,
): Promise<boolean> {
  try {
    return await DataService.savePrintConfig(printName, {
      transforms,
      productTransforms,
      lastModified: Date.now(),
    });
  } catch (e) {
    console.error('Ошибка сохранения конфига:', e);
    return false;
  }
}

/** Get transform for the correct mode */
export function getTransformByMode(
  transforms: Record<string, Transform>,
  productTransforms: Record<string, Transform>,
  mode: string,
  productId: string,
  fallbackScale = 0.5,
): Transform {
  const map = mode === 'products' ? productTransforms : transforms;
  return map[productId] || { x: 0, y: 0, scale: fallbackScale, rotation: 0 };
}
